import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { formatINR } from '@/lib/tracks';
import {
  Atom,
  FlaskConical,
  Calculator,
  Dna,
  BookOpenText,
  ExternalLink,
  Lock,
  ShoppingCart,
  Sparkles,
  Loader2,
  ArrowLeft,
  CheckCircle2,
  Flame,
} from 'lucide-react';

const SUBJECT_META = {
  Physics: { Icon: Atom, tag: 'PYQs + concept sheets', accent: 'from-red-500/30 to-transparent' },
  Chemistry: { Icon: FlaskConical, tag: 'Named reactions + NCERT drills', accent: 'from-red-500/30 to-transparent' },
  Maths: { Icon: Calculator, tag: 'Solved sets + formula master list', accent: 'from-red-500/30 to-transparent' },
  Biology: { Icon: Dna, tag: 'Diagrams + one-liners for revision', accent: 'from-red-500/30 to-transparent' },
  English: { Icon: BookOpenText, tag: 'Board-pattern writing skills + literature', accent: 'from-red-500/30 to-transparent' },
};

export default function QbankPage() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    api
      .get('/qbank')
      .then(({ data }) => setData(data))
      .catch(() => toast.error('Failed to load Q-Bank'))
      .finally(() => setLoading(false));
  }, []);

  const openLink = (url, label) => {
    if (!url) {
      toast.error(`${label} coming soon — admin will add it shortly.`);
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const buyPlan = async () => {
    setBuying(true);
    try {
      const { data: order } = await api.post('/orders', { plan_id: 'BOARDS_QBANK' });
      nav(`/checkout/${order.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start checkout');
      setBuying(false);
    }
  };

  if (loading || !data) {
    return (
      <AppShell>
        <div className="grid min-h-[40vh] place-items-center text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </AppShell>
    );
  }

  const unlocked = data.unlocked;

  return (
    <AppShell>
      <div className="space-y-6" data-testid="qbank-page">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => nav('/dashboard')} className="gap-1.5" data-testid="qbank-back">
            <ArrowLeft className="h-4 w-4" />
            Back to dashboard
          </Button>
        </div>

        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl border border-red-500/25 bg-gradient-to-br from-red-500/15 via-red-500/5 to-transparent p-6 sm:p-8">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-red-500/25 blur-[100px]"
          />
          <div className="relative flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-2xl">
              <div className="mb-2 flex items-center gap-2">
                <Badge className="gap-1 bg-red-600 text-white shadow-[0_0_16px_rgba(239,68,68,0.5)]" data-testid="highest-seller-badge">
                  <Flame className="h-3.5 w-3.5" />
                  Highest Seller
                </Badge>
                <Badge variant="outline">Class 12</Badge>
              </div>
              <h1 className="font-display text-3xl font-bold sm:text-4xl">
                Boards Q-Bank
              </h1>
              <p className="mt-2 text-sm text-muted-foreground sm:text-base">
                Curated Class 12 question banks — Physics · Chemistry · Maths · Biology · English. Every PDF is
                hand-picked, aligned to the latest CBSE pattern, and hosted on Google Drive so you can revise anywhere.
              </p>
            </div>
            <div className="rounded-2xl border bg-background/60 p-4 backdrop-blur">
              {unlocked ? (
                <div className="flex items-center gap-2 text-emerald-600" data-testid="qbank-unlocked-banner">
                  <CheckCircle2 className="h-5 w-5" />
                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">Status</div>
                    <div className="font-semibold">Unlocked — enjoy!</div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">One-time · Class 12</div>
                  <div className="font-display text-2xl font-bold">{formatINR(data.price || 999)}</div>
                  <Button
                    onClick={buyPlan}
                    disabled={buying}
                    className="w-full gap-1.5 bg-red-600 hover:bg-red-500"
                    data-testid="qbank-buy-button"
                  >
                    {buying ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
                    Buy for {formatINR(data.price || 999)}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Subject grid */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.items.map((it) => {
            const meta = SUBJECT_META[it.subject] || {};
            const Icon = meta.Icon || BookOpenText;
            return (
              <Card
                key={it.subject}
                className={`group relative overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-lg ${
                  it.locked ? 'opacity-95' : ''
                }`}
                data-testid={`qbank-card-${it.subject}`}
              >
                <div
                  aria-hidden
                  className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${meta.accent || ''}`}
                />
                <CardContent className="relative space-y-4 p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <div className="grid h-11 w-11 place-items-center rounded-lg bg-red-600 text-white shadow-[0_0_18px_rgba(239,68,68,0.4)]">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="font-display text-lg font-semibold">{it.subject}</div>
                        <div className="text-xs text-muted-foreground">{meta.tag || 'Curated question bank'}</div>
                      </div>
                    </div>
                    {!it.has_content && (
                      <Badge variant="outline" className="text-[10px]">
                        Coming soon
                      </Badge>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Button
                      variant="outline"
                      className="w-full gap-1.5"
                      onClick={() => openLink(it.sample_url, 'Sample')}
                      disabled={!it.sample_url}
                      data-testid={`qbank-sample-${it.subject}`}
                    >
                      <Sparkles className="h-4 w-4" />
                      {it.sample_url ? 'View Sample' : 'Sample coming soon'}
                    </Button>

                    {it.locked ? (
                      <div
                        className="flex items-center justify-center gap-1.5 rounded-md border border-dashed border-red-500/40 bg-red-500/5 px-3 py-2 text-xs text-red-500"
                        data-testid={`qbank-locked-hint-${it.subject}`}
                      >
                        <Lock className="h-3.5 w-3.5" />
                        Buy Q-Bank once to unlock all subjects
                      </div>
                    ) : (
                      <Button
                        className="w-full gap-1.5 bg-red-600 hover:bg-red-500"
                        onClick={() => openLink(it.drive_url, `${it.subject} Q-Bank`)}
                        disabled={!it.drive_url}
                        data-testid={`qbank-open-${it.subject}`}
                      >
                        <ExternalLink className="h-4 w-4" />
                        {it.drive_url ? `Open ${it.subject} Q-Bank` : 'Content coming soon'}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Single unified buy CTA — one payment unlocks ALL 5 subjects */}
        {!unlocked && (
          <div
            className="sticky bottom-4 z-40 mx-auto max-w-3xl rounded-2xl border border-red-500/30 bg-neutral-950/95 p-4 shadow-[0_20px_50px_-15px_rgba(239,68,68,0.5)] backdrop-blur"
            data-testid="qbank-unified-buy-bar"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Flame className="h-4 w-4 text-red-500" />
                  <div className="font-display text-base font-semibold text-white">
                    One payment · unlocks all 5 subjects
                  </div>
                </div>
                <div className="mt-0.5 text-xs text-neutral-400">
                  Physics · Chemistry · Maths · Biology · English — instant Drive access for 1 full year.
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500">One-time</div>
                  <div className="font-display text-xl font-bold text-white">
                    {formatINR(data.price || 999)}
                  </div>
                </div>
                <Button
                  onClick={buyPlan}
                  disabled={buying}
                  className="h-11 gap-1.5 bg-red-600 px-5 text-base font-semibold hover:bg-red-500"
                  data-testid="qbank-unified-buy-button"
                >
                  {buying ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
                  Unlock all subjects
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
